import { DataProvider, HttpError } from "@refinedev/core";
import axios, { AxiosError } from "axios";
import { TOKEN_KEY } from "./authProvider";
import { API_URL } from "./configs";

const axiosInstance = axios.create();
const apiUrl = API_URL;

// add token to every request
axiosInstance.interceptors.request.use(
  async (config) => {
    config.baseURL = apiUrl;

    const token = localStorage.getItem(TOKEN_KEY);

    if (token && config?.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const dataProvider = (): DataProvider => ({
  getOne: async ({ id, resource, meta }) => {
    try {
      const params = meta?.params;

      let response;

      if (meta?.skipId) {
        response = await axiosInstance.get(`${resource}`, {
          params,
        });
      } else {
        response = await axiosInstance.get(`${resource}/${id}`, {
          params,
        });
      }

      let data = await response.data;

      if (data?.data) {
        data = data.data;
      }

      return {
        data,
      };
    } catch (e: unknown) {
      const response = (e as AxiosError)?.response;
      const actualError = (response?.data as { message: string })?.message;

      const customError: HttpError = {
        message: actualError || "An error occurred",
        statusCode: response?.status || 500,
      };

      throw customError;
    }
  },
  create: async ({ resource, variables }) => {
    try {
      const response = await axiosInstance.post(resource, variables);

      const data = await response.data;

      return {
        data,
      };
    } catch (e: unknown) {
      const response = (e as AxiosError)?.response;
      const actualError = (response?.data as { message: string })?.message;

      const customError: HttpError = {
        message: actualError || "An error occurred",
        statusCode: response?.status || 500,
      };

      throw customError;
    }
  },
  update: async ({ resource, id, variables }) => {
    try {
      const response = await axiosInstance.patch(
        `${resource}/${id}`,
        variables
      );
      const data = await response.data;

      return {
        data,
      };
    } catch (e: unknown) {
      const response = (e as AxiosError)?.response;
      const actualError = (response?.data as { message: string })?.message;

      const customError: HttpError = {
        message: actualError || "An error occurred",
        statusCode: response?.status || 500,
      };

      throw customError;
    }
  },
  deleteOne: async ({ resource, id, meta }) => {
    try {
      const soft = meta?.softDelete || false;

      const response = await axiosInstance.delete(`${resource}/${id}`, {
        params: { soft },
      });

      const data = await response.data;

      return {
        data,
      };
    } catch (e: unknown) {
      const response = (e as AxiosError)?.response;
      const actualError = (response?.data as { message: string })?.message;

      const customError: HttpError = {
        message: actualError || "An error occurred",
        statusCode: response?.status || 500,
      };

      throw customError;
    }
  },
  getList: async ({ resource, pagination, meta }) => {
    try {
      const response = await axiosInstance.get(resource, {
        params: {
          page: pagination?.current,
          ...meta?.params,
        },
      });

      const data = await response.data.data;

      return {
        data: data.items,
        total: data.total,
        extra: data.extra,
        hasNext: data.hasNext,
      };
    } catch (e: unknown) {
      const response = (e as AxiosError)?.response;
      const actualError = (response?.data as { message: string })?.message;

      const customError: HttpError = {
        message: actualError || "An error occurred",
        statusCode: response?.status || 500,
      };

      throw customError;
    }
  },
  getApiUrl: () => apiUrl,
});
